import { useState, useEffect, useRef, useCallback } from 'react';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { beatsApi, userApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  MapPin, Navigation, Clock, Users, RefreshCw, Loader2,
  ChevronRight, Gauge, Eye, Satellite, Radio,
  Play, Pause, SkipForward, SkipBack, Calendar, Lock
} from 'lucide-react';

const REFRESH_INTERVAL = 15000;
const MAP_TILE = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

/* ─── SVG Animated Bike Rider (Zomato-style) ─── */
const BIKE_RIDER_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="48" height="48">
  <style>
    .wheel { animation: spin 0.6s linear infinite; transform-origin: center; }
    .rider { animation: bounce 0.4s ease-in-out infinite alternate; }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes bounce { to { transform: translateY(-1.5px); } }
  </style>
  <!-- Back wheel -->
  <g class="wheel" style="transform-origin: 16px 48px;">
    <circle cx="16" cy="48" r="8" fill="none" stroke="#374151" stroke-width="3"/>
    <circle cx="16" cy="48" r="2" fill="#374151"/>
  </g>
  <!-- Front wheel -->
  <g class="wheel" style="transform-origin: 48px 48px;">
    <circle cx="48" cy="48" r="8" fill="none" stroke="#374151" stroke-width="3"/>
    <circle cx="48" cy="48" r="2" fill="#374151"/>
  </g>
  <!-- Frame -->
  <path d="M16 48 L28 32 L48 48 M28 32 L38 32 L48 48" fill="none" stroke="#1d4ed8" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="38" y1="32" x2="42" y2="24" stroke="#1d4ed8" stroke-width="2" stroke-linecap="round"/>
  <!-- Handlebars -->
  <path d="M40 23 L44 21 L48 23" fill="none" stroke="#374151" stroke-width="2" stroke-linecap="round"/>
  <!-- Seat -->
  <rect x="25" y="29" width="8" height="3" rx="1.5" fill="#374151"/>
  <!-- Rider body -->
  <g class="rider">
    <circle cx="30" cy="18" r="5" fill="#f59e0b"/>
    <path d="M30 23 L30 32" stroke="#1d4ed8" stroke-width="3" stroke-linecap="round"/>
    <path d="M30 26 L24 30" stroke="#1d4ed8" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M30 26 L38 28" stroke="#1d4ed8" stroke-width="2.5" stroke-linecap="round"/>
    <!-- Helmet -->
    <path d="M25 16 Q25 10 30 10 Q35 10 35 16" fill="#ef4444" stroke="#b91c1c" stroke-width="1"/>
    <rect x="25" y="15" width="10" height="2" rx="1" fill="#ef4444"/>
    <!-- Visor -->
    <rect x="31" y="14" width="5" height="3" rx="1" fill="#1e293b" opacity="0.7"/>
  </g>
</svg>`;

const BIKE_RIDER_DATA_URI = `data:image/svg+xml;base64,${typeof btoa !== 'undefined' ? btoa(BIKE_RIDER_SVG) : ''}`;

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function formatTime(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

export default function LiveTrackingPage() {
  const { user: authUser, isOwner } = useAuth();
  const [liveUsers, setLiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [trail, setTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);
  const [salespersons, setSalespersons] = useState([]);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef({});
  const trailLayerRef = useRef(null);
  const intervalRef = useRef(null);

  // Replay state
  const [replayMode, setReplayMode] = useState(false);
  const [replayUser, setReplayUser] = useState('');
  const [replayDate, setReplayDate] = useState(new Date().toISOString().split('T')[0]);
  const [replayData, setReplayData] = useState(null);
  const [replayIdx, setReplayIdx] = useState(0);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(1);
  const [replayLoading, setReplayLoading] = useState(false);
  const replayTimerRef = useRef(null);
  const replayMarkerRef = useRef(null);
  const replayTrailRef = useRef(null);

  // Check if user has enterprise access
  const isEnterprise = authUser?.subscription?.tier === 'enterprise' || isOwner;

  const fetchLiveData = useCallback(async () => {
    try {
      const res = await beatsApi.getLiveUsers();
      setLiveUsers(res.data.users || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchLiveData();
    intervalRef.current = setInterval(fetchLiveData, REFRESH_INTERVAL);
    userApi.getSalespersons().then(r => setSalespersons(r.data || [])).catch(() => {});
    return () => clearInterval(intervalRef.current);
  }, [fetchLiveData]);

  // Load Leaflet
  useEffect(() => {
    if (mapInstance.current) return;
    const load = async () => {
      if (window.L) { initMap(window.L); return; }
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => initMap(window.L);
      document.head.appendChild(script);
    };
    const initMap = (L) => {
      if (!mapRef.current || mapInstance.current) return;
      const map = L.map(mapRef.current).setView([20.5937, 78.9629], 5);
      L.tileLayer(MAP_TILE, { attribution: '', maxZoom: 19 }).addTo(map);
      mapInstance.current = map;
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update live markers
  useEffect(() => {
    const L = window.L;
    const map = mapInstance.current;
    if (!L || !map || replayMode) return;

    Object.values(markersRef.current).forEach(m => map.removeLayer(m));
    markersRef.current = {};

    if (liveUsers.length === 0) return;
    const bounds = [];

    liveUsers.forEach(u => {
      if (!u.lat || !u.lng) return;
      const isOnBeat = u.on_beat;

      // Use bike rider SVG for on-beat users
      const icon = isOnBeat
        ? L.icon({ iconUrl: BIKE_RIDER_DATA_URI, iconSize: [48, 48], iconAnchor: [24, 40], popupAnchor: [0, -40], className: 'bike-rider-icon' })
        : L.divIcon({
            className: 'online-marker',
            html: `<div style="position:relative;width:36px;height:36px;">
              <div style="position:absolute;inset:0;background:#60a5fa;border-radius:50%;opacity:0.3;animation:pulse 2s infinite;"></div>
              <div style="position:absolute;top:4px;left:4px;width:28px;height:28px;background:#3b82f6;border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
                <span style="color:white;font-size:11px;font-weight:bold;">${u.name.charAt(0)}</span>
              </div>
            </div>`,
            iconSize: [36, 36], iconAnchor: [18, 18],
          });

      const popup = `<div style="min-width:180px;font-family:system-ui;">
        <div style="font-weight:600;font-size:14px;">${u.name}</div>
        <div style="font-size:11px;color:#666;margin:2px 0 6px;">${u.role} &bull; ${u.email}</div>
        <span style="background:${isOnBeat ? '#dcfce7' : '#dbeafe'};color:${isOnBeat ? '#166534' : '#1d4ed8'};padding:2px 8px;border-radius:10px;font-size:11px;font-weight:500;">${isOnBeat ? 'On Beat' : 'Online'}</span>
        <span style="color:#999;font-size:11px;margin-left:6px;">${timeAgo(u.last_updated)}</span>
        ${u.beat_info ? `<div style="margin-top:6px;font-size:11px;color:#666;">${u.beat_info.total_pings || 0} pings &bull; ${(u.beat_info.distance_km || 0).toFixed(1)} km</div>` : ''}
        <div style="margin-top:3px;font-size:10px;color:#999;">${u.lat.toFixed(5)}, ${u.lng.toFixed(5)}</div>
      </div>`;

      const marker = L.marker([u.lat, u.lng], { icon }).bindPopup(popup);
      marker.addTo(map);
      markersRef.current[u.user_id] = marker;
      bounds.push([u.lat, u.lng]);
    });

    if (bounds.length > 0) map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
  }, [liveUsers, replayMode]);

  // ─── REPLAY FUNCTIONS ───

  const loadReplayTrail = async () => {
    if (!replayUser || !replayDate) { toast.error('Select a user and date'); return; }
    setReplayLoading(true);
    try {
      const res = await beatsApi.getLocationTrail({ user_id: replayUser, date_from: replayDate, date_to: replayDate });
      const data = res.data;
      if (!data.points || data.points.length === 0) {
        toast.error(`No location data for ${data.user_name} on ${replayDate}`);
        setReplayLoading(false);
        return;
      }
      setReplayData(data);
      setReplayIdx(0);
      setReplayMode(true);
      setReplayPlaying(false);
      toast.success(`Loaded ${data.count} points for ${data.user_name}`);

      // Clear live markers, draw full trail
      const L = window.L;
      const map = mapInstance.current;
      if (L && map) {
        Object.values(markersRef.current).forEach(m => map.removeLayer(m));
        markersRef.current = {};
        if (trailLayerRef.current) map.removeLayer(trailLayerRef.current);
        if (replayTrailRef.current) map.removeLayer(replayTrailRef.current);
        if (replayMarkerRef.current) map.removeLayer(replayMarkerRef.current);

        const coords = data.points.map(p => [p.lat, p.lng]);
        replayTrailRef.current = L.polyline(coords, {
          color: '#6366f1', weight: 3, opacity: 0.4, dashArray: '6, 8',
        }).addTo(map);

        // Place bike rider at start
        const startIcon = L.icon({ iconUrl: BIKE_RIDER_DATA_URI, iconSize: [56, 56], iconAnchor: [28, 48], popupAnchor: [0, -48], className: 'bike-rider-replay' });
        replayMarkerRef.current = L.marker(coords[0], { icon: startIcon, zIndexOffset: 1000 })
          .bindPopup(`<b>${data.user_name}</b><br/>Start: ${formatTime(data.points[0].timestamp)}`)
          .addTo(map);

        map.fitBounds(replayTrailRef.current.getBounds(), { padding: [60, 60] });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load trail');
    } finally { setReplayLoading(false); }
  };

  const exitReplay = () => {
    setReplayMode(false);
    setReplayPlaying(false);
    setReplayData(null);
    setReplayIdx(0);
    if (replayTimerRef.current) clearInterval(replayTimerRef.current);
    const map = mapInstance.current;
    if (map) {
      if (replayTrailRef.current) { map.removeLayer(replayTrailRef.current); replayTrailRef.current = null; }
      if (replayMarkerRef.current) { map.removeLayer(replayMarkerRef.current); replayMarkerRef.current = null; }
    }
    fetchLiveData();
  };

  // Replay animation loop
  useEffect(() => {
    if (!replayPlaying || !replayData) return;
    const L = window.L;
    const map = mapInstance.current;
    if (!L || !map) return;

    replayTimerRef.current = setInterval(() => {
      setReplayIdx(prev => {
        const next = prev + 1;
        if (next >= replayData.points.length) {
          setReplayPlaying(false);
          clearInterval(replayTimerRef.current);
          return prev;
        }
        const pt = replayData.points[next];
        if (replayMarkerRef.current) {
          replayMarkerRef.current.setLatLng([pt.lat, pt.lng]);
          replayMarkerRef.current.setPopupContent(
            `<b>${replayData.user_name}</b><br/>${formatTime(pt.timestamp)}<br/><span style="font-size:10px;color:#666;">${pt.lat.toFixed(5)}, ${pt.lng.toFixed(5)}</span>`
          );
          map.panTo([pt.lat, pt.lng], { animate: true, duration: 0.3 });
        }
        return next;
      });
    }, 1000 / replaySpeed);

    return () => clearInterval(replayTimerRef.current);
  }, [replayPlaying, replaySpeed, replayData]);

  const togglePlay = () => {
    if (replayIdx >= (replayData?.points?.length || 0) - 1) setReplayIdx(0);
    setReplayPlaying(!replayPlaying);
  };

  const skipForward = () => {
    if (!replayData) return;
    const next = Math.min(replayIdx + Math.max(10, Math.floor(replayData.points.length / 10)), replayData.points.length - 1);
    setReplayIdx(next);
    const pt = replayData.points[next];
    if (replayMarkerRef.current && pt) {
      replayMarkerRef.current.setLatLng([pt.lat, pt.lng]);
      mapInstance.current?.panTo([pt.lat, pt.lng]);
    }
  };

  const skipBack = () => {
    if (!replayData) return;
    const prev = Math.max(replayIdx - Math.max(10, Math.floor(replayData.points.length / 10)), 0);
    setReplayIdx(prev);
    const pt = replayData.points[prev];
    if (replayMarkerRef.current && pt) {
      replayMarkerRef.current.setLatLng([pt.lat, pt.lng]);
      mapInstance.current?.panTo([pt.lat, pt.lng]);
    }
  };

  const onBeatCount = liveUsers.filter(u => u.on_beat).length;
  const onlineCount = liveUsers.filter(u => !u.on_beat).length;
  const progress = replayData ? ((replayIdx / Math.max(replayData.points.length - 1, 1)) * 100) : 0;

  return (
    <MainLayout>
      <div className="space-y-4" data-testid="live-tracking-page">
        <style>{`
          @keyframes pulse { 0% { transform: scale(1); opacity: 0.3; } 50% { transform: scale(1.8); opacity: 0; } 100% { transform: scale(1); opacity: 0; } }
          .bike-rider-icon { filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)); transition: transform 0.3s; }
          .bike-rider-replay { filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4)); z-index: 10000 !important; }
        `}</style>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Satellite className="w-6 h-6 text-emerald-500" />
              Live GPS Tracking
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {replayMode ? `Replaying ${replayData?.user_name}'s trail — ${replayDate}` : 'Real-time location of all team members'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!replayMode && (
              <>
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
                  <Radio className="w-3 h-3 animate-pulse" /> {onBeatCount} On Beat
                </Badge>
                <Badge className="bg-blue-100 text-blue-700 border-blue-200 gap-1">
                  <MapPin className="w-3 h-3" /> {onlineCount} Online
                </Badge>
              </>
            )}
            {replayMode && (
              <Button variant="outline" size="sm" onClick={exitReplay} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="exit-replay-btn">
                Exit Replay
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchLiveData(); }} data-testid="refresh-tracking-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Map */}
          <Card className="lg:col-span-3 border-gray-200" data-testid="tracking-map-card">
            <CardContent className="p-0 relative">
              <div ref={mapRef} className="w-full h-[520px] rounded-lg" data-testid="live-map" />

              {/* Replay Controls Overlay */}
              {replayMode && replayData && (
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg p-3 z-[1000]" data-testid="replay-controls">
                  {/* Progress bar */}
                  <div className="relative w-full h-2 bg-gray-200 rounded-full mb-3 cursor-pointer"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pct = (e.clientX - rect.left) / rect.width;
                      const idx = Math.floor(pct * (replayData.points.length - 1));
                      setReplayIdx(idx);
                      const pt = replayData.points[idx];
                      if (replayMarkerRef.current && pt) {
                        replayMarkerRef.current.setLatLng([pt.lat, pt.lng]);
                        mapInstance.current?.panTo([pt.lat, pt.lng]);
                      }
                    }}
                    data-testid="replay-progress-bar"
                  >
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-200" style={{ width: `${progress}%` }} />
                    <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-indigo-500 rounded-full shadow-md transition-all duration-200" style={{ left: `calc(${progress}% - 8px)` }} />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={skipBack} data-testid="replay-skip-back">
                        <SkipBack className="w-4 h-4" />
                      </Button>
                      <Button size="sm" className={`h-9 w-9 p-0 rounded-full ${replayPlaying ? 'bg-red-500 hover:bg-red-600' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                        onClick={togglePlay} data-testid="replay-play-btn">
                        {replayPlaying ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={skipForward} data-testid="replay-skip-forward">
                        <SkipForward className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="text-xs text-gray-600 font-medium">
                      {replayData.points[replayIdx] ? formatTime(replayData.points[replayIdx].timestamp) : '--'}
                      <span className="text-gray-400 mx-1">/</span>
                      {replayData.points.length > 0 ? formatTime(replayData.points[replayData.points.length - 1].timestamp) : '--'}
                    </div>

                    <div className="flex items-center gap-1">
                      {[1, 2, 5, 10].map(s => (
                        <Button key={s} variant={replaySpeed === s ? 'default' : 'outline'} size="sm"
                          className={`h-7 text-xs px-2 ${replaySpeed === s ? 'bg-indigo-600 text-white' : ''}`}
                          onClick={() => setReplaySpeed(s)} data-testid={`speed-${s}x`}>
                          {s}x
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-1 text-[10px] text-gray-400">
                    <span>Point {replayIdx + 1} of {replayData.points.length}</span>
                    <span>{replayData.user_name} &bull; {replayDate}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Side Panel */}
          <div className="space-y-3">
            {/* Live Users Panel */}
            {!replayMode && (
              <Card className="border-gray-200">
                <CardContent className="p-3">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-blue-500" /> Team Members ({liveUsers.length})
                  </h3>
                  {loading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
                  ) : liveUsers.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      <MapPin className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p>No team members online</p>
                      <p className="text-xs mt-1">Locations appear when members open the app</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {liveUsers.map(u => (
                        <div key={u.user_id}
                          className={`p-2.5 rounded-lg cursor-pointer transition-all border ${selectedUser === u.user_id ? 'border-blue-300 bg-blue-50 shadow-sm' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}
                          onClick={() => {
                            if (selectedUser === u.user_id) { setSelectedUser(null); return; }
                            setSelectedUser(u.user_id);
                            if (mapInstance.current && u.lat && u.lng) {
                              mapInstance.current.setView([u.lat, u.lng], 15, { animate: true });
                              markersRef.current[u.user_id]?.openPopup();
                            }
                          }}
                          data-testid={`user-card-${u.user_id}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${u.on_beat ? 'bg-emerald-500' : 'bg-blue-500'}`}>
                              {u.name.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-medium text-gray-800 truncate">{u.name}</span>
                                <div className={`w-1.5 h-1.5 rounded-full ${u.on_beat ? 'bg-emerald-400 animate-pulse' : 'bg-blue-400'}`} />
                              </div>
                              <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                <span>{u.role}</span>
                                <span>{timeAgo(u.last_updated)}</span>
                              </div>
                            </div>
                            {u.on_beat && <ChevronRight className="w-4 h-4 text-gray-300" />}
                          </div>
                          {u.beat_info && (
                            <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-500 pl-10">
                              <span className="flex items-center gap-0.5"><Navigation className="w-3 h-3" />{(u.beat_info.distance_km || 0).toFixed(1)} km</span>
                              <span className="flex items-center gap-0.5"><Gauge className="w-3 h-3" />{u.beat_info.total_pings || 0} pings</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Trail Replay Panel */}
            <Card className={`border-gray-200 ${!isEnterprise ? 'opacity-75' : ''}`} data-testid="replay-panel">
              <CardContent className="p-3">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                  <Eye className="w-4 h-4 text-indigo-500" /> Trail Replay
                  {!isEnterprise && <Lock className="w-3 h-3 text-amber-500 ml-1" />}
                </h3>

                {isEnterprise ? (
                  <div className="space-y-2">
                    <Select value={replayUser} onValueChange={setReplayUser}>
                      <SelectTrigger className="h-8 text-xs" data-testid="replay-user-select">
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                      <SelectContent>
                        {salespersons.map(sp => (
                          <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <input
                      type="date"
                      value={replayDate}
                      onChange={(e) => setReplayDate(e.target.value)}
                      className="w-full h-8 text-xs border border-gray-200 rounded-md px-2 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                      data-testid="replay-date-picker"
                    />

                    <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white gap-1"
                      onClick={loadReplayTrail} disabled={replayLoading || !replayUser}
                      data-testid="load-replay-btn">
                      {replayLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                      {replayLoading ? 'Loading...' : 'Load Trail'}
                    </Button>

                    {replayData && (
                      <div className="pt-2 border-t border-gray-100 space-y-1 text-xs text-gray-500">
                        <div className="flex justify-between"><span>Points</span><span className="font-medium text-gray-700">{replayData.count}</span></div>
                        <div className="flex justify-between"><span>First ping</span><span>{replayData.points[0] ? formatTime(replayData.points[0].timestamp) : '--'}</span></div>
                        <div className="flex justify-between"><span>Last ping</span><span>{replayData.points.length > 0 ? formatTime(replayData.points[replayData.points.length - 1].timestamp) : '--'}</span></div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Lock className="w-6 h-6 mx-auto text-amber-400 mb-2" />
                    <p className="text-xs text-gray-500 mb-2">Trail Replay is an Enterprise feature</p>
                    <p className="text-[10px] text-gray-400">Watch animated replays of your team's entire day movement</p>
                    <Button size="sm" variant="outline" className="mt-2 text-xs border-amber-300 text-amber-600 hover:bg-amber-50"
                      onClick={() => window.location.href = '/subscription'} data-testid="upgrade-replay-btn">
                      Upgrade to Enterprise
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
